import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { restEndpoint } from 'config';
import { ProductModel, ApiModel } from 'models';
import { DynamicFeatureFlagService } from 'shared/services';
import { ImageResolution } from 'helpers';
import { DownloadCsvOption } from 'app/content/models/download.model';
import { BulkSearchResultWithInformation } from 'app/content/models/product.model';
import { ENABLE_UNIFIED_CSV_DOWNLOAD_FLAG } from 'app/content/constants/csv-download.constants';

@Injectable()
export class ProductService {
    constructor(
        private productModel: ProductModel,
        private apiModel: ApiModel,
        private dynamicFeatureFlagService: DynamicFeatureFlagService
    ) {}

    public validateGtins(gtins: string[]): { valid: string[]; invalid: string[] } {
        const valid = _.filter(gtins, this.isValidGtin);
        const invalid = _.difference(gtins, valid);

        return { valid, invalid };
    }

    public bulkDownload(params: BulkDownloadParams): Observable<BulkSearchResultWithInformation> {
        return this.isUnifiedCsvDownloadEnabled$().pipe(
            switchMap((enabled) =>
                enabled ? this.bulkDownloadInOneRequest(params) : this.bulkDownloadWithSeparateCsvRequest(params)
            )
        );
    }

    public bulkImagesDownload({
        gtins,
        articleNumbers,
        orderId,
        size,
        initiator,
        orderType,
        images = true,
        video = false
    }: {
        gtins?: string[];
        articleNumbers?: string[];
        orderId?: string;
        size: ImageResolution;
        initiator: string;
        orderType?: string;
        images?: boolean;
        video?: boolean;
    }): Observable<BulkSearchResultWithInformation> {
        return this.productModel
            .bulkGetProductImages({ gtins, articleNumbers, orderId, size, initiator, orderType, images, video })
            .pipe(
                map((response) => ({
                    downloadUrl: _.isEmpty(response.downloadId)
                        ? ''
                        : `${restEndpoint}/internal${this.apiModel.getDownloadsEndpoint()}/${response.downloadId}`,
                    additionalData: response.additionalData,
                    downloadInformation: response.downloadInformation,
                    tooManyGtins: false
                })),
                catchError(this.mapTooManyGtinsError)
            );
    }

    public bulkImagesDownloadWithCsv(
        params: BulkImagesDownloadWithCsvParams
    ): Observable<BulkSearchResultWithInformation> {
        return this.isUnifiedCsvDownloadEnabled$().pipe(
            switchMap((enabled) =>
                enabled
                    ? this.bulkImagesDownloadWithCsvInOneRequest(params)
                    : this.bulkImagesDownloadWithSeparateCsvRequest(params)
            )
        );
    }

    public getPvhAllowedBrands(): Observable<string[]> {
        return this.productModel.getPvhAllowedBrands();
    }

    public checkIfBrandHasOrderedArticles(brandId: string): Observable<boolean> {
        return this.apiModel.makeHttpObservableRequest<boolean>({
            method: 'GET',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/has-ordered`
        });
    }

    private bulkDownloadInOneRequest({
        gtins,
        articleNumbers,
        orderId,
        userLanguage,
        size,
        csvLanguage,
        initiator,
        orderType
    }: BulkDownloadParams): Observable<BulkSearchResultWithInformation> {
        return this.productModel
            .bulkGetProductImages({
                gtins,
                articleNumbers,
                orderId,
                size,
                initiator,
                orderType,
                images: false,
                csv: this.getCsvOption(userLanguage, csvLanguage)
            })
            .pipe(
                map(({ downloadId, additionalData, downloadInformation }) => ({
                    downloadUrl: this.getDownloadUrl(downloadId, csvLanguage),
                    ...(additionalData ? { additionalData } : {}),
                    ...(downloadInformation ? { downloadInformation } : {}),
                    tooManyGtins: false
                })),
                catchError(this.mapTooManyGtinsError)
            );
    }

    private bulkDownloadWithSeparateCsvRequest({
        gtins,
        articleNumbers,
        orderId,
        userLanguage,
        size,
        csvLanguage,
        initiator,
        orderType
    }: BulkDownloadParams): Observable<BulkSearchResultWithInformation> {
        return this.productModel
            .bulkGetProductData({ gtins, articleNumbers, orderId, language: userLanguage, size, initiator, orderType })
            .pipe(
                map(({ downloadId }) => ({
                    downloadUrl: this.getCsvDownloadUrl(downloadId, csvLanguage),
                    tooManyGtins: false
                })),
                catchError(this.mapTooManyGtinsError)
            );
    }

    private bulkImagesDownloadWithCsvInOneRequest({
        gtins,
        articleNumbers,
        orderId,
        userLanguage,
        size,
        csvLanguage,
        initiator,
        orderType,
        images = true,
        video = false
    }: BulkImagesDownloadWithCsvParams): Observable<BulkSearchResultWithInformation> {
        return this.productModel
            .bulkGetProductImages({
                gtins,
                articleNumbers,
                orderId,
                size,
                initiator,
                orderType,
                images,
                video,
                csv: this.getCsvOption(userLanguage, csvLanguage)
            })
            .pipe(
                map(({ downloadId, additionalData, downloadInformation }) => ({
                    downloadUrl: this.getDownloadUrl(downloadId, csvLanguage),
                    ...(additionalData ? { additionalData } : {}),
                    ...(downloadInformation ? { downloadInformation } : {}),
                    tooManyGtins: false
                })),
                catchError(this.mapTooManyGtinsError)
            );
    }

    private bulkImagesDownloadWithSeparateCsvRequest({
        gtins,
        articleNumbers,
        orderId,
        userLanguage,
        size,
        csvLanguage,
        initiator,
        orderType,
        images = true,
        video = false
    }: BulkImagesDownloadWithCsvParams): Observable<BulkSearchResultWithInformation> {
        return combineLatest([
            this.productModel.bulkGetProductImages({
                gtins,
                articleNumbers,
                orderId,
                size,
                initiator,
                orderType,
                images,
                video
            }),
            this.productModel.bulkGetProductData({
                gtins,
                articleNumbers,
                orderId,
                language: userLanguage,
                size,
                initiator,
                orderType
            })
        ]).pipe(
            map(([{ downloadId, additionalData, downloadInformation }, { downloadId: csvDownloadId }]) => ({
                downloadUrl: this.getImagesWithCsvDownloadUrl(downloadId, csvDownloadId, csvLanguage),
                ...(additionalData ? { additionalData } : {}),
                ...(downloadInformation ? { downloadInformation } : {}),
                tooManyGtins: false
            })),
            catchError(this.mapTooManyGtinsError)
        );
    }

    private isUnifiedCsvDownloadEnabled$(): Observable<boolean> {
        return this.dynamicFeatureFlagService.isFeatureEnabled$(ENABLE_UNIFIED_CSV_DOWNLOAD_FLAG).pipe(take(1));
    }

    private isValidGtin(gtin: string): boolean {
        return _.isString(gtin) && /^([0-9]{8}|[0-9]{12,14})$/.test(gtin);
    }

    private getCsvDownloadUrl(downloadId?: string, csvLanguage?: string): string {
        return _.isEmpty(downloadId)
            ? ''
            : `${restEndpoint}/internal${this.apiModel.getProductsEndpoint()}/data/download/allowed/${downloadId}${
                  csvLanguage === 'all' ? '?lang=all' : ''
              }`;
    }

    private getImagesWithCsvDownloadUrl(downloadId?: string, csvDownloadId?: string, csvLanguage?: string): string {
        return _.isEmpty(downloadId)
            ? this.getCsvDownloadUrl(csvDownloadId, csvLanguage)
            : `${restEndpoint}/internal${this.apiModel.getDownloadsEndpoint()}/${downloadId}?csvToken=${csvDownloadId}${
                  csvLanguage === 'all' ? '&lang=all' : ''
              }`;
    }

    private getCsvOption(userLanguage: string, csvLanguage?: string): DownloadCsvOption {
        return { include: true, language: csvLanguage === 'all' ? 'all' : userLanguage };
    }

    private getDownloadUrl(downloadId?: string, csvLanguage?: string): string {
        return _.isEmpty(downloadId)
            ? ''
            : `${restEndpoint}/internal${this.apiModel.getDownloadsEndpoint()}/${downloadId}${
                  csvLanguage === 'all' ? '?lang=all' : ''
              }`;
    }

    private mapTooManyGtinsError(error: { data: { type: string } } | any) {
        if (error?.data?.type === 'TooManyGtinsError') {
            return of({
                downloadUrl: '',
                tooManyGtins: true
            });
        }

        return throwError(error);
    }
}

interface BulkDownloadParams {
    gtins?: string[];
    articleNumbers?: string[];
    orderId?: string;
    userLanguage: string;
    size: ImageResolution;
    csvLanguage?: string;
    initiator: string;
    orderType?: string;
}

interface BulkImagesDownloadWithCsvParams extends BulkDownloadParams {
    images?: boolean;
    video?: boolean;
}
